import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $getNearestNodeFromDOMNode } from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { $isCustomTableCellNode } from '../nodes/CustomTableCellNode';

type MousePosition = {
  x: number;
  y: number;
};

type Direction = 'right' | 'bottom';

const MIN_ROW_HEIGHT = 18;
const MIN_COLUMN_WIDTH = 62;

function TableCellResizer(): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [activeCell, setActiveCell] = useState<HTMLTableCellElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<Direction | null>(null);
  const [startPosition, setStartPosition] = useState<MousePosition>({ x: 0, y: 0 });
  const [hoveredCell, setHoveredCell] = useState<HTMLTableCellElement | null>(null);

  // Update DOM styles only (fast, no Lexical re-render)
  const updateRowHeightDOM = useCallback((cell: HTMLTableCellElement, deltaY: number) => {
    const row = cell.parentElement;
    if (!row) {
      return;
    }

    const currentHeight = row.offsetHeight;
    const newHeight = Math.max(MIN_ROW_HEIGHT, currentHeight + deltaY);

    // Set height on all cells in the row (DOM only)
    const cells = Array.from(row.children) as HTMLTableCellElement[];
    cells.forEach((c) => {
      c.style.height = `${newHeight}px`;
    });
  }, []);

  // Persist height to Lexical state (called once after drag ends)
  const persistRowHeight = useCallback((cell: HTMLTableCellElement) => {
    editor.update(() => {
      const row = cell.parentElement;
      if (!row) {
        return;
      }

      const cells = Array.from(row.children) as HTMLTableCellElement[];
      cells.forEach((c) => {
        const cellNode = $getNearestNodeFromDOMNode(c);
        if (cellNode && $isCustomTableCellNode(cellNode)) {
          const writableNode = cellNode.getWritable();
          const currentHeight = parseInt(c.style.height) || c.offsetHeight;
          writableNode.setHeight(currentHeight);
        }
      });
    });
  }, [editor]);

  // Update DOM styles only (fast, no Lexical re-render)
  const updateColumnWidthDOM = useCallback((cell: HTMLTableCellElement, deltaX: number) => {
    const table = cell.closest('table');
    if (!table) {
      return;
    }

    const cellIndex = Array.from(cell.parentElement?.children || []).indexOf(cell);
    const currentWidth = cell.offsetWidth;
    const newWidth = Math.max(MIN_COLUMN_WIDTH, currentWidth + deltaX);

    // Set width on all cells in the column (DOM only)
    const rows = Array.from(table.querySelectorAll('tr'));
    rows.forEach((row) => {
      const cells = Array.from(row.children) as HTMLTableCellElement[];
      if (cells[cellIndex]) {
        cells[cellIndex].style.width = `${newWidth}px`;
      }
    });
  }, []);

  // Persist width to Lexical state (called once after drag ends)
  const persistColumnWidth = useCallback((cell: HTMLTableCellElement) => {
    editor.update(() => {
      const table = cell.closest('table');
      if (!table) {
        return;
      }

      const cellIndex = Array.from(cell.parentElement?.children || []).indexOf(cell);
      const rows = Array.from(table.querySelectorAll('tr'));
      rows.forEach((row) => {
        const cells = Array.from(row.children) as HTMLTableCellElement[];
        if (cells[cellIndex]) {
          const cellNode = $getNearestNodeFromDOMNode(cells[cellIndex]);
          if (cellNode && $isCustomTableCellNode(cellNode)) {
            const writableNode = cellNode.getWritable();
            const currentWidth = parseInt(cells[cellIndex].style.width) || cells[cellIndex].offsetWidth;
            writableNode.setWidth(currentWidth);
          }
        }
      });
    });
  }, [editor]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent, cell: HTMLTableCellElement, direction: Direction) => {
      event.preventDefault();
      event.stopPropagation();
      setIsResizing(true);
      setResizeDirection(direction);
      setActiveCell(cell);
      setStartPosition({ x: event.clientX, y: event.clientY });
    },
    []
  );

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!activeCell || !resizeDirection) {
        return;
      }

      const deltaX = event.clientX - startPosition.x;
      const deltaY = event.clientY - startPosition.y;

      // Update DOM only during drag (no Lexical re-render = smooth performance)
      if (resizeDirection === 'right') {
        updateColumnWidthDOM(activeCell, deltaX);
      } else if (resizeDirection === 'bottom') {
        updateRowHeightDOM(activeCell, deltaY);
      }

      setStartPosition({ x: event.clientX, y: event.clientY });
    };

    const handlePointerUp = () => {
      // Persist final dimensions to Lexical state when drag ends
      if (activeCell && resizeDirection) {
        if (resizeDirection === 'right') {
          persistColumnWidth(activeCell);
        } else if (resizeDirection === 'bottom') {
          persistRowHeight(activeCell);
        }
      }

      setIsResizing(false);
      setResizeDirection(null);
      setActiveCell(null);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isResizing, activeCell, resizeDirection, startPosition, updateColumnWidthDOM, updateRowHeightDOM, persistColumnWidth, persistRowHeight]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (isResizing) {
        return;
      }

      const target = event.target as HTMLElement;

      // Check if we're hovering over a resize handle
      const isOverResizeHandle =
        target.classList.contains('table-cell-resize-handle') ||
        target.closest('.table-cell-resize-handle') !== null;

      // If hovering over a resize handle, keep the current hovered cell
      if (isOverResizeHandle) {
        return;
      }

      // Otherwise, detect which cell we're over
      const cell = target.closest('td, th') as HTMLTableCellElement;

      if (cell && cell.closest('.editor-input')) {
        setHoveredCell(cell);
      } else {
        setHoveredCell(null);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isResizing]);

  if (!hoveredCell) {
    return <></>;
  }

  const rect = hoveredCell.getBoundingClientRect();

  return createPortal(
    <>
      {/* Right resize handle */}
      <div
        className="table-cell-resize-handle table-cell-resize-handle-right"
        style={{
          position: 'fixed',
          left: `${rect.right - 4}px`,
          top: `${rect.top}px`,
          width: '8px',
          height: `${rect.height}px`,
          cursor: 'col-resize',
          zIndex: 1000,
        }}
        onPointerDown={(e) => handlePointerDown(e, hoveredCell, 'right')}
      />

      {/* Bottom resize handle */}
      <div
        className="table-cell-resize-handle table-cell-resize-handle-bottom"
        style={{
          position: 'fixed',
          left: `${rect.left}px`,
          top: `${rect.bottom - 4}px`,
          width: `${rect.width}px`,
          height: '8px',
          cursor: 'row-resize',
          zIndex: 1000,
        }}
        onPointerDown={(e) => handlePointerDown(e, hoveredCell, 'bottom')}
      />
    </>,
    document.body
  );
}

export default function TableCellResizerPlugin(): React.JSX.Element | null {
  const isEditable = useLexicalEditable();
  return isEditable ? <TableCellResizer /> : null;
}
